import { useCallback } from 'react';
import { produce } from 'immer';

// Try relative import
import useStore from '../store/store';
import { ChatInterface } from '../types/chat';
import { createMessage } from '../utils/chatNavigation';

export function useMessageManager() {
  const setChats = useStore((state: any) => state.setChats);
  const currentChatIndex = useStore((state: any) => state.currentChatIndex);

  // Detect Apple Silicon and adjust accordingly
  const isAppleSilicon = navigator.userAgent.includes('Mac') && 
                         navigator.hardwareConcurrency >= 8;

  // Use immer for efficient immutable updates
  const appendToLastMessage = useCallback((content: string) => {
    const updateFn = () => {
      // Use Zustand's set function directly with immer-style updates
      useStore.setState((state) => ({
        ...state,
        chats: produce(state.chats, (draft: ChatInterface[]) => {
          if (!Array.isArray(draft)) {
            console.error('🚨 Draft is not an array:', draft);
            return;
          }
          
          if (currentChatIndex < 0 || currentChatIndex >= draft.length) {
            console.error('🚨 Invalid currentChatIndex:', currentChatIndex, 'length:', draft.length);
            return;
          }
          
          const currentChat = draft[currentChatIndex];
          if (!currentChat?.messages || !Array.isArray(currentChat.messages)) {
            console.error('🚨 Invalid messages array:', currentChat);
            return;
          }
          
          if (currentChat.messages.length > 0) {
            const lastMessage = currentChat.messages[currentChat.messages.length - 1];
            if (lastMessage?.role === 'assistant') {
              lastMessage.content += content;
            }
          }
        })
      }));
    };

    if (isAppleSilicon && 'requestIdleCallback' in window) {
      requestIdleCallback(updateFn, { timeout: 16 });
    } else {
      updateFn();
    }
  }, [currentChatIndex, isAppleSilicon]);
  
  // Add flush method for final cleanup
  const flushMessageBuffer = useCallback(() => {
    setChats((currentChats: ChatInterface[]) => {
      return produce(currentChats, (draft: ChatInterface[]) => {
        if (currentChatIndex >= 0 && currentChatIndex < draft.length) {
          const currentChat = draft[currentChatIndex];
          if (currentChat?.messages && currentChat.messages.length > 0) {
            const lastMessage = currentChat.messages[currentChat.messages.length - 1];
            if (lastMessage?.contentBuffer) {
              lastMessage.content = lastMessage.contentBuffer.join('');
              delete lastMessage.contentBuffer;
            }
          }
        }
      });
    });
  }, [currentChatIndex, setChats]);
  
  // Get store state safely
  const getStoreState = useCallback(() => {
    const state = useStore.getState();
    
    // Ensure chats is always an array
    if (!Array.isArray(state.chats)) {
      console.warn('⚠️ Chats is not an array, initializing:', state.chats);
      // Fix the state immediately
      useStore.setState({ chats: [] });
      return {
        ...state,
        chats: [],
        currentChatIndex: Math.max(0, state.currentChatIndex)
      };
    }
    
    if (state.currentChatIndex < 0 && state.chats.length > 0) {
      console.warn('⚠️ Invalid currentChatIndex, fixing');
      useStore.setState({ currentChatIndex: 0 });
    }
    
    return state;
  }, []);
  
  // Add method to create initial assistant message
  const appendAssistantMessage = useCallback((
    chats: ChatInterface[],
    chatIndex: number
  ): ChatInterface[] => {
    return produce(chats, (draft: ChatInterface[]) => {
      if (chatIndex >= 0 && chatIndex < draft.length && draft[chatIndex]) {
        draft[chatIndex].messages.push(createMessage('assistant', ''));
      }
    });
  }, []);

  return {
    appendToLastMessage,
    appendAssistantMessage,
    flushMessageBuffer,
    getStoreState,
    setChats
  };
}